import styled from '@emotion/styled';
import { tokens, Icon, Text } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { trackClick } from '@hotjar/tracking';

import { EVENT_UI_POSITIONS } from '../../constants-types/TrackEventUIPositions';
import { EVENT_NAMES } from '../../constants-types/TrackEventNames';
import { getTrackGroupDataAndTriggeredOnState } from '../../utils/trackingUtils';
import type { TSupportSecondaryView } from '../../constants-types/SmartPanelSecondaryViews';
import { SMART_PANEL_SECONDARY_VIEWS } from '../../constants-types/SmartPanelSecondaryViews';
import { SmartPanelSection, SmartPanelSegment } from './SmartPanelBlocks';

import type { TrackableSite } from '../../constants-types/ExternalDependencyTypes';

interface SupportSectionProps {
  currentSite?: TrackableSite;
  onActivateSecondarySection: (activeView: TSupportSecondaryView) => void;
}

const StyledIcon = styled(Icon)`
  margin-left: ${tokens.spacer.x2};
`;

const SupportOption = styled(SmartPanelSegment)`
  align-items: center;
  cursor: pointer;
  justify-content: space-between;

  &:hover {
    background-color: ${tokens.color.bg_hover};
  }
`;

const ChevronIcon = () => <StyledIcon icon="chevron_right" />;

export const SupportSection = ({
  currentSite,
  onActivateSecondarySection,
}: SupportSectionProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');

  const handleOptionClick = (elementName: string, activeView: TSupportSecondaryView) => {
    if (currentSite !== undefined) {
      trackClick({
        name: elementName,
        uiPosition: EVENT_UI_POSITIONS.SMART_PANEL,
        ...getTrackGroupDataAndTriggeredOnState(currentSite),
      });
    }

    onActivateSecondarySection(activeView);
  };

  return (
    <SmartPanelSection>
      <SupportOption
        role="button"
        onClick={() =>
          handleOptionClick(
            EVENT_NAMES.HM_DOES_NOT_LOOK_RIGHT,
            SMART_PANEL_SECONDARY_VIEWS.HM_DOES_NOT_LOOK_RIGHT,
          )
        }
      >
        <Text as="span">{t('smart-panel.support.does-not-look-right')}</Text>
        <ChevronIcon />
      </SupportOption>
      <SupportOption
        role="button"
        onClick={() =>
          handleOptionClick(
            EVENT_NAMES.HM_NOT_CAPTURING_URL,
            SMART_PANEL_SECONDARY_VIEWS.HM_NOT_CAPTURING_URL,
          )
        }
      >
        <Text as="span">{t('smart-panel.support.not-capturing-right-url')}</Text>
        <ChevronIcon />
      </SupportOption>
      <SupportOption
        role="button"
        onClick={() =>
          handleOptionClick(
            EVENT_NAMES.HM_SHOWS_COOKIES_BANNER,
            SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER,
          )
        }
        keepLastBorder
      >
        <Text as="span">{t('smart-panel.support.cookies-banners')}</Text>
        <ChevronIcon />
      </SupportOption>
    </SmartPanelSection>
  );
};
